import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Eye, Wand2, Loader2 } from 'lucide-react';
import { Prompt } from '../../types'; // Keep this import
import { useClickOutside } from '../../hooks/useClickOutside';
import { openAIService } from '../../services/openai';
import { useScrollLock } from '../../hooks/useScrollLock';

interface PromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void;
  onUpdate?: (id: string, updates: Partial<Prompt>) => void;
  prompt?: Prompt | null;
  defaultFolderId?: string | null;
}

export const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  prompt,
  defaultFolderId,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    isFavorite: false,
    isTemplate: false,
    folderId: null as string | null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  
  // Auto-title generation state
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [hasUserEditedTitle, setHasUserEditedTitle] = useState(false);
  const [lastBodyForTitle, setLastBodyForTitle] = useState('');
  const [titleGenerationError, setTitleGenerationError] = useState<string | null>(null);

  // Move generateTitleFromBody outside useEffect to avoid stale closures
  const generateTitleFromBody = useCallback(async () => {
    if (!formData.body.trim() || isGeneratingTitle) return;

    console.log('🔵 Auto-generating title for body:', formData.body.substring(0, 50) + '...');
    console.log('🔵 Environment check:', {
      hasApiKey: !!import.meta.env.VITE_OPENAI_API_KEY,
      apiKeyLength: import.meta.env.VITE_OPENAI_API_KEY?.length || 0,
      environment: import.meta.env.MODE
    });
    
    setIsGeneratingTitle(true);
    setTitleGenerationError(null);
    setLastBodyForTitle(formData.body);

    try {
      const generatedTitle = await openAIService.generateTitle(formData.body);
      console.log('✅ Generated title:', generatedTitle);
      
      // Only update if user hasn't manually edited the title
      if (!hasUserEditedTitle) {
        setFormData(prev => ({ ...prev, title: generatedTitle }));
      }
    } catch (error) {
      console.error('🔴 Error generating title:', error);
      
      // Set error state so user knows what happened
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate title';
      setTitleGenerationError(errorMessage);
      
      // Log detailed error for debugging
      console.error('🔴 Title generation detailed error:', {
        error,
        message: errorMessage,
        hasApiKey: !!import.meta.env.VITE_OPENAI_API_KEY,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsGeneratingTitle(false);
    }
  }, [formData.body, isGeneratingTitle, hasUserEditedTitle]);

  const modalRef = useClickOutside(() => {
    if (isOpen && !isLoading && !isGeneratingTitle) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const extractUniqueVariables = (text: string): string[] => {
    const matches = text.match(/\[([^\]]+)\]/g);
    if (!matches) return [];
    
    // Get unique variable names and sort them
    const uniqueVariables = [...new Set(matches.map(match => match.slice(1, -1)))];
    return uniqueVariables.sort();
  };

  useEffect(() => {
    if (prompt) {
      const hasVariables = extractUniqueVariables(prompt.body).length > 0;
      setFormData({
        title: prompt.title,
        body: prompt.body,
        isFavorite: prompt.isFavorite,
        isTemplate: hasVariables,
        folderId: prompt.folderId || null,
      });
      setShowPreview(hasVariables);
      setHasUserEditedTitle(true);
      setLastBodyForTitle(prompt.body);
    } else {
      setFormData({
        title: '',
        body: '',
        isFavorite: false,
        isTemplate: false,
        folderId: defaultFolderId || null,
      });
      setShowPreview(true);
      setHasUserEditedTitle(false);
      setLastBodyForTitle('');
    }
  }, [prompt, isOpen, defaultFolderId]);

  // Automatic template detection based on variables in the body
  useEffect(() => {
    if (isOpen) {
      const hasVariables = extractUniqueVariables(formData.body).length > 0;
      if (hasVariables !== formData.isTemplate) {
        setFormData(prev => ({ ...prev, isTemplate: hasVariables }));
      }
      if (hasVariables) {
        setShowPreview(true);
      }
    }
  }, [formData.body, isOpen]);

  // Auto-generate title when body changes (for new prompts only)
  useEffect(() => {
    const shouldGenerateTitle = 
      isOpen && 
      !prompt && // Only for new prompts
      !hasUserEditedTitle && 
      formData.body.trim().length > 5 && // Reduced minimum length
      formData.body !== lastBodyForTitle && // Body has actually changed
      !isGeneratingTitle; // Not already generating

    console.log('🔵 Title generation check:', {
      isOpen,
      isNewPrompt: !prompt,
      hasUserEditedTitle,
      bodyLength: formData.body.trim().length,
      bodyChanged: formData.body !== lastBodyForTitle,
      isGeneratingTitle,
      shouldGenerateTitle
    });

    if (shouldGenerateTitle) {
      console.log('🔵 Triggering auto title generation...');
      generateTitleFromBody();
    }
  }, [formData.body, isOpen, prompt, hasUserEditedTitle, lastBodyForTitle, isGeneratingTitle, generateTitleFromBody]);

  const handleManualTitleGeneration = async () => {
    if (!formData.body.trim() || isGeneratingTitle) return;

    console.log('🔵 Manual title generation triggered for:', formData.body.substring(0, 50) + '...');
    console.log('🔵 Manual generation environment check:', {
      hasApiKey: !!import.meta.env.VITE_OPENAI_API_KEY,
      apiKeyLength: import.meta.env.VITE_OPENAI_API_KEY?.length || 0
    });
    
    setIsGeneratingTitle(true);
    setTitleGenerationError(null);

    try {
      const generatedTitle = await openAIService.generateTitle(formData.body);
      console.log('✅ Manual generation successful:', generatedTitle);
      setFormData(prev => ({ ...prev, title: generatedTitle }));
      setHasUserEditedTitle(false); // Reset so auto-generation can work again
    } catch (error) {
      console.error('🔴 Manual generation error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate title';
      setTitleGenerationError(errorMessage);
      
      // Show user-friendly error message
      if (errorMessage.includes('API key')) {
        alert('OpenAI API key is not configured. Title generation is not available.');
      } else {
        alert(`Failed to generate title: ${errorMessage}`);
      }
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setFormData({ ...formData, title: newTitle });
    
    // Mark as user-edited if they're actually typing (not just clearing)
    if (newTitle.length > 0) {
      console.log('🔵 User manually edited title, disabling auto-generation');
      setHasUserEditedTitle(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Allow saving as long as there's text in either field
    if (!formData.title.trim() && !formData.body.trim()) {
      return;
    }
    
    // If no title but there's body content, generate a simple title
    let finalTitle = formData.title.trim();
    if (!finalTitle && formData.body.trim()) {
      finalTitle = 'Untitled Prompt';
    }

    setIsLoading(true);
    try {
      if (prompt && onUpdate) {
        await onUpdate(prompt.id, { ...formData, title: finalTitle });
      } else {
        await onSave({ ...formData, title: finalTitle });
      }
      onClose();
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const extractVariablesWithCounts = (text: string): { name: string; count: number }[] => {
    const matches = text.match(/\[([^\]]+)\]/g);
    if (!matches) return [];

    const variableCounts: Record<string, number> = {};
    matches.forEach(match => {
      const variable = match.slice(1, -1);
      variableCounts[variable] = (variableCounts[variable] || 0) + 1;
    });

    return Object.entries(variableCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const variablesWithCounts = extractVariablesWithCounts(formData.body);
  const totalVariableCount = variablesWithCounts.reduce((sum, variable) => sum + variable.count, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
            {prompt ? 'Edit Prompt' : 'New Prompt'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title with AI Generation */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Title
              </label>
              {!prompt && formData.body.trim().length > 20 && (
                <button
                  type="button"
                  onClick={handleManualTitleGeneration}
                  disabled={isGeneratingTitle}
                  className="flex items-center px-3 py-1 text-xs text-blue-600 dark:text-blue-400 
                    hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 
                    dark:hover:bg-blue-900/20 rounded-lg transition-all duration-300 
                    disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200 
                    dark:border-blue-800"
                  title="Generate title from prompt content"
                >
                  {isGeneratingTitle ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-3 w-3 mr-1" />
                      Generate Title
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={handleTitleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text"
                placeholder="Enter prompt title (optional)..."
              />
              {isGeneratingTitle && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                </div>
              )}
            </div>
            {!prompt && formData.body.trim().length > 5 && !formData.title.trim() && isGeneratingTitle && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center">
                <Wand2 className="h-3 w-3 mr-1" /> {/* Keep this icon */}
                Title will auto-generate as you type your prompt
              </p>
            )}
            
            {/* Auto-generation error display */}
            {!prompt && titleGenerationError && !isGeneratingTitle && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center">
                <Wand2 className="h-3 w-3 mr-1" />
                Auto-generation unavailable: {titleGenerationError.includes('API key') ? 'OpenAI API key not configured' : 'Connection error'}
              </p>
            )}
            
            {/* Error display for title generation */}
            {titleGenerationError && (
              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-xs text-red-600 dark:text-red-400">
                  <strong>Title generation failed:</strong> {titleGenerationError}
                </p>
                {titleGenerationError.includes('API key') && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                    This feature requires an OpenAI API key to be configured.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Prompt Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="body" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Prompt Content
              </label>
              {variablesWithCounts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center text-sm text-fire-600 dark:text-fire-400 hover:text-fire-700 dark:hover:text-fire-300 transition-colors duration-300"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {showPreview ? 'Hide' : 'Show'} Variables
                </button>
              )}
            </div>
            <textarea
              id="body"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent min-h-[200px] bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text"
              placeholder="Enter your prompt content here..."
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Use square brackets to define variables: [object], [style], [color], etc. Templates are automatically detected.
              </p>
              {!prompt && formData.body.trim().length > 20 && !formData.title.trim() && (
                <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center"> {/* Keep this text */}
                  <Wand2 className="h-3 w-3 mr-1" />
                  Auto-generating title...
                </p>
              )}
            </div>
          </div>

          {/* Variables Preview */}
          {showPreview && variablesWithCounts.length > 0 && (
            <div className="bg-modular-canvas-fog dark:bg-modular-shadow-quartz/20 border border-modular-stone-alloy dark:border-modular-graphite-smoke rounded-lg p-4 transition-all duration-300">
              <h4 className="text-sm font-medium text-modular-frosted-indigo dark:text-modular-frosted-indigo mb-3 flex items-center">
                <Wand2 className="h-4 w-4 mr-2" /> {/* Keep this icon */}
                Template Variables Detected ({variablesWithCounts.length} unique, {totalVariableCount} total) {/* Keep this text */}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {variablesWithCounts.map((variable, index) => (
                  <div
                    key={`${variable.name}-${index}`}
                    className="inline-flex items-center justify-between px-3 py-2 rounded-lg text-sm font-mono bg-fire-100 dark:bg-fire-900/30 text-fire-800 dark:text-fire-300 border border-fire-200 dark:border-fire-700"
                  >
                    <div className="flex items-center"> {/* Keep this div */}
                      <span className="text-modular-frosted-indigo dark:text-modular-frosted-indigo mr-1">[</span>
                      {variable.name}
                      <span className="text-modular-frosted-indigo dark:text-modular-frosted-indigo ml-1">]</span>
                    </div>
                    {variable.count > 1 && (
                      <span className="ml-2 text-xs bg-fire-200 dark:bg-fire-800 text-fire-700 dark:text-fire-300 px-1.5 py-0.5 rounded-full font-semibold">
                        x{variable.count}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 bg-modular-canvas-fog dark:bg-modular-shadow-quartz/20 rounded-lg border border-modular-stone-alloy dark:border-modular-graphite-smoke">
                <p className="text-xs text-modular-frosted-indigo dark:text-modular-frosted-indigo font-medium mb-1">
                  ✨ This prompt will be automatically saved as a template
                </p>
                <p className="text-xs text-modular-frosted-indigo dark:text-modular-frosted-indigo"> {/* Keep this p */}
                  {variablesWithCounts.length === 1 
                    ? `This prompt has 1 unique variable${totalVariableCount > 1 ? ` used ${totalVariableCount} times` : ''} that can be customized when executing the template.`
                    : `This prompt has ${variablesWithCounts.length} unique variables${totalVariableCount > variablesWithCounts.length ? ` (${totalVariableCount} total uses)` : ''} that can be customized when executing the template.`
                  }
                </p>
              </div>
            </div>
          )}

          {/* Favorite Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="favorite"
              checked={formData.isFavorite} // Keep this logic
              onChange={(e) => setFormData({ ...formData, isFavorite: e.target.checked })}
              className="rounded border-gray-300 dark:border-dark-border text-fire-600 focus:ring-fire-500 mr-2"
            />
            <label htmlFor="favorite" className="text-sm text-gray-700 dark:text-gray-300">
              Mark as favorite
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-dark-border">
            <button
              type="button"
              onClick={onClose} // Keep this action
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || (!formData.title.trim() && !formData.body.trim())}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold shadow-lg"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {prompt ? 'Update' : 'Save'} Prompt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};